import { DriverAuthenticationClient, DriverAuthenticationManager } from '@frontend/authentication-v2';
import { EntityType, Locale } from '@frontend/common';
import { useLocale } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { useBlobRepository, useBrandingRepository } from '@frontend/repository';
import { SettingKey } from '@frontend/setting/types';
import { ScreenType } from '@frontend/wizard/types';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useIdentification, useScanners } from '../hooks';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { UserInterfaceNavigationState, startUserSession } from '../redux/terminal-navigation-slice';
import { useKioskNavigate } from '../utils/kiosk-navigation';

interface ViewProps {
    accountId?: string | null;
    user: UserInterfaceNavigationState['user'];
    homeScreenType: ScreenType | null;
    spotLogoLoading: boolean;
    spotLogoUrl: string | null;
    spotLogoNotFound: boolean;
    brandingLogo: string;
    kioskRefresh: () => void;
    scannerResolving: boolean;
    stopResolving: () => void;
    error: string | null;
    resetError: () => void;
    setInfoModalOpen: (open: boolean) => void;
}

const useHome = (): ViewProps => {
    const intl = useIntl();
    const { locale, changeLocale } = useLocale();
    const dispatch = useAppDispatch();
    const navigate = useKioskNavigate();
    const { logo_navbar_expanded } = useBrandingRepository();
    const blobRepository = useBlobRepository();
    const navigationState = useAppSelector((store) => store.navigation);
    const accountId = navigationState.iot?.account_id;
    const spotSettings = navigationState.spot?.settings;
    const homeScreenType = spotSettings ? (spotSettings[SettingKey.HOME_SCREEN_TYPE] as ScreenType) : null;
    const spotId = navigationState.iot?.spot_id;
    const cachedSpotLogo = accountId && spotId ? blobRepository.getBlobStale(accountId, EntityType.SPOT, spotId) : null;
    const cachedNotFound = accountId && spotId ? blobRepository.isBlobNotFound(accountId, EntityType.SPOT, spotId) : false;
    const [spotLogoLoading, setSpotLogoLoading] = useState<boolean>(!cachedSpotLogo && !cachedNotFound);
    const [spotLogoUrl, setSpotLogoUrl] = useState<string | null>(cachedSpotLogo?.url ?? null);
    const [spotLogoNotFound, setSpotLogoNotFound] = useState<boolean>(cachedNotFound);

    const [scannerResolving, setScannerResolving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [infoModalOpen, setInfoModalOpen] = useState<boolean>(false);

    useScanners({
        id: 'HOME_SCANNER',
        onDeviceSensorReceived: (code: string) => setScannerResolving(true)
    });

    useIdentification({
        id: 'HOME_IOT_IDENTIFICATION',
        onNoMatchingIdentificationFound: () => {
            setScannerResolving(false);
            setError(intl.formatMessage({ id: 'HOME.ERRORS.PLEASE_TRY_AGAIN', defaultMessage: 'Please try again.' }));
        },
        onUnlinkedIdentificationFound: () => {
            setScannerResolving(false);
            setError(
                intl.formatMessage({
                    id: 'kiosk.home.unlinked-identification',
                    description: 'Error shown when a scanned code is recognized but not linked to anything',
                    defaultMessage: 'This code was recognized but is not linked to anything yet. Please try a different code or contact support.'
                })
            );
        },
        onShowWizard: (wizardId, functionalityId, data) => navigate(`/wizard/${functionalityId}/${wizardId}/`, { state: data }),
        onUserIdentified: (user, args) => {
            //TODO: add better error handling - user does not know what is happening here
            if (spotSettings?.[SettingKey.ALLOW_USER_IDENTIFICATION] === undefined) {
                setScannerResolving(false);
                return;
            }

            const accountId = navigationState.iot?.account_id;
            const iotId = navigationState.iot?.id;
            if (!accountId || !iotId) {
                Logger.error('Missing account_id or iot_id, thus cannot login the user.');
                setScannerResolving(false);
                return;
            }

            const handleIdentification = async () => {
                try {
                    const token = await DriverAuthenticationManager.getInstance().waitForToken();
                    let auth = await DriverAuthenticationClient.authorizeUser(accountId, iotId, user.id, token).catch((e) => null);
                    if (!auth) {
                        const info = DriverAuthenticationManager.getInstance().provisionInfo;
                        if (info) {
                            await DriverAuthenticationManager.getInstance().authenticate(info);
                            auth = await DriverAuthenticationClient.authorizeUser(accountId, iotId, user.id, token).catch((e) => null);
                        }
                    }

                    if (!auth) return false;

                    dispatch(startUserSession({ id: user.id, accountId, auth, user }));
                    return true;
                } catch (error) {
                    Logger.error('Error during identification:', error);
                    return false;
                }
            };
            handleIdentification().finally(() => {
                setScannerResolving(false);
            });
        }
    });

    useEffect(() => {
        let isDisposed = false;

        if (accountId && spotId) {
            const cached = blobRepository.getBlobStale(accountId, EntityType.SPOT, spotId);
            if (cached) {
                setSpotLogoUrl(cached.url);
                setSpotLogoNotFound(false);
                setSpotLogoLoading(false);
                blobRepository.refreshBlob(accountId, EntityType.SPOT, spotId);
            } else if (blobRepository.isBlobNotFound(accountId, EntityType.SPOT, spotId)) {
                setSpotLogoUrl(null);
                setSpotLogoNotFound(true);
                setSpotLogoLoading(false);
                blobRepository.refreshBlob(accountId, EntityType.SPOT, spotId);
            } else {
                setSpotLogoLoading(true);
                blobRepository
                    .getBlob(accountId, EntityType.SPOT, spotId)
                    .then((info) => {
                        if (isDisposed) return;
                        setSpotLogoUrl(info?.url ?? null);
                        setSpotLogoNotFound(!info);
                    })
                    .catch(() => {
                        if (isDisposed) return;
                        setSpotLogoUrl(null);
                        setSpotLogoNotFound(true);
                    })
                    .finally(() => {
                        if (!isDisposed) setSpotLogoLoading(false);
                    });
            }
        } else {
            setSpotLogoLoading(false);
            setSpotLogoUrl(null);
            setSpotLogoNotFound(false);
        }

        return () => {
            isDisposed = true;
        };
    }, [accountId, spotId]);

    useEffect(() => {
        let timeout: NodeJS.Timeout | null = null;
        //Afchecken wanneer we dit precies wel of niet willen doen (alle scenarios) want altijd op home is misschien toch best vervelend
        if (!infoModalOpen && !navigationState.user && navigationState.iotSettings && navigationState.iotSettings[SettingKey.DEFAULT_LANGUAGE]) {
            const defaultLanguage = navigationState.iotSettings[SettingKey.DEFAULT_LANGUAGE] as string;
            if (locale !== defaultLanguage) {
                timeout = setTimeout(() => {
                    changeLocale(defaultLanguage as Locale);
                }, 1000 * 30); // Adding a delay to ensure that the home screen is fully loaded before changing the language
            }
        }

        return () => {
            if (timeout) clearTimeout(timeout);
        };
    }, [navigationState.iotSettings, navigationState.user, infoModalOpen]);

    const kioskRefresh = () => {
        DriverAuthenticationManager.getInstance().logout();
        window.location.reload();
    };

    return {
        accountId,
        user: navigationState.user,
        homeScreenType,
        spotLogoLoading,
        spotLogoUrl,
        spotLogoNotFound,
        brandingLogo: logo_navbar_expanded,
        kioskRefresh,
        scannerResolving,
        stopResolving: () => setScannerResolving(false),
        error,
        resetError: () => setError(null),
        setInfoModalOpen
    };
};

export default useHome;
